import { Directive, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ElementRef } from '@angular/core';
import { Input, Output } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { filter, debounceTime, pluck, map  } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { ViewInterceptorEvent } from '../../models/view-interceptor-directive/view-interceptor-event.interface';
import { ViewInterceptorEventsNamesEnum } from '../../enum/view-interceptor-directive/view-interceptor-events-names.enum';

@Directive({
    selector: '[viewInterceptor]',
})
export class ViewInterceptorDirective implements OnInit, OnDestroy {

    @Input('beforeSize') public beforeSize: number = 0;
    @Input('afterSize') public afterSize: number = 0;
    @Input('debounce') public debounce: number = 0;
    @Input('disableInterceptor') public disable: boolean = false;

    @Output('snapshot') public snapshot: Subject<ViewInterceptorEvent> = new Subject<ViewInterceptorEvent>();
    @Output('onShow') public onShow: Subject<ViewInterceptorEvent> = new Subject<ViewInterceptorEvent>();
    @Output('onHide') public onHide: Subject<ViewInterceptorEvent> = new Subject<ViewInterceptorEvent>();

    private element: HTMLElement;
    private subscription: Subscription;
    private isShowing: boolean;

    constructor(
        public elementRef: ElementRef,
    ) {
        this.element = this.elementRef.nativeElement;

        this.subscription = fromEvent(window, 'scroll')
            .pipe(
                filter(() => !this.disable),
                debounceTime(this.debounce),
                pluck('target'),
                map((target) => (target as any).defaultView || target),
            )
            .subscribe((windowElement: Window) => {
                const isOnScreen = this.checkElementIsOnScreen(windowElement);
                this.nextShowOrHide(isOnScreen);
            });
    }

    public ngOnInit() {
        if (this.disable) return;
        this.getSnapshot();
    }

    /**
     * Verifica se o elemento com a diretiva está visivel.
     */
    public checkElementIsOnScreen(windowElement: Window): boolean {
        const scrollBottom = windowElement.scrollY + windowElement.innerHeight;
        const scrollTop = windowElement.scrollY;
        const elementRects: ClientRect = this.element.getClientRects()
            .item(0);
        if (!elementRects) return false;
        const elementTop = (scrollTop + elementRects.top) - (+this.beforeSize);
        const elementBottom = elementTop + elementRects.height + (+this.afterSize);

        return scrollBottom >= elementTop && scrollTop < elementBottom;
    }

    /**
     * Evia o output de show ou hide com base na visibilidade do elemento.
     * @param isOnScreen Visibilidade do elemento.
     */
    public nextShowOrHide(isOnScreen: boolean) {

        if (isOnScreen !== this.isShowing) {
            this.onShow.next(new ViewInterceptorEvent(ViewInterceptorEventsNamesEnum.OnShow, isOnScreen, this.element));
            this.onHide.next(new ViewInterceptorEvent(ViewInterceptorEventsNamesEnum.OnHide, !isOnScreen, this.element));
        }

        this.isShowing = isOnScreen;
    }

    /**
     * Pega um snapshop do elemento caso seja pedido
     */
    public getSnapshot() {
        const isOnScreen = this.checkElementIsOnScreen(window);
        this.snapshot.next(new ViewInterceptorEvent(ViewInterceptorEventsNamesEnum.Snapshot, isOnScreen, this.element));
        this.snapshot.complete();
        this.isShowing = isOnScreen;
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }

}
